import { Box } from '@components/basics/container';
import { Icons } from '@components/themes/default';
import { Icon } from '@components/basics/icon';
import { IBox } from '@components/basics/container/Box/IBox';
import React, { useContext } from 'react';
import { ThemeContext } from 'styled-components';
import customWindow from '@components/interfaces/global';
import { openShareWindow } from '@components/helpers/utils';

interface ISharingMenu extends IBox.BaseProps {
	sharingUrl?: string;
	sharingTitle?: string;
	sharingDescription?: string;
	type: 'facebook' | 'twitter' | 'whatsapp' | 'mail' | 'print';
}

interface ISharingButton {
	label?: string;
	title?: string;
	icon?: string;
	actionUrl?: string;
	action?: (sharingButtonElement?: HTMLElement) => void;
}

const actionUrlFacebook = (url: string, title: string) => {
	return `https://www.facebook.com/sharer.php?u=${url}&t=${encodeURIComponent(title)}`;
};

const actionUrlTwitter = (url: string, title: string) => {
	return `https://x.com/intent/tweet?text=${encodeURIComponent(title)}&url=${url}`;
};

const actionUrlWhatsApp = (url: string, title: string) => {
	return `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`;
};

const actionUrlMail = (url: string, title: string, description: string) => {
	const subject = `Link-Tipp: ${title}`;
	const body = `Link-Tipp: ${title} ${encodeURIComponent(`\r\r ${description}`)} %0D%0A%0D%0A${url}`;

	return `mailto:?subject=${subject}&body=${body}`;
};

const SharingOption: React.FC<ISharingMenu> = ({ sharingUrl, sharingTitle, sharingDescription, type }): JSX.Element => {
	const themeContext = useContext(ThemeContext);

	const sharingButton = ({ label, title, icon, actionUrl, action }: ISharingButton) => {
		let sharingButtonElement: HTMLElement;

		const setRef = (element: HTMLElement) => {
			sharingButtonElement = element;
		};

		return (
			<Box
				// needs to be an <a> anchor element to be focusable in some environments
				as="a"
				role="button"
				target="_self"
				rel="nofollow"
				ref={setRef}
				cursor="pointer"
				tabIndex={0 as never}
				display="flex"
				alignItems="center"
				justifyContent="center"
				width="100%"
				padding="0.5rem"
				aria-label={title}
				title={title}
				position="relative"
				backgroundColor={themeContext.sharingMenu.menu.button.backgroundColor}
				customCss={{
					':hover': { backgroundColor: themeContext.sharingMenu.trigger.button.backgroundColorHover },
					':focus': { textDecoration: 'none' },
					':focus-visible': { backgroundColor: themeContext.sharingMenu.menu.button.backgroundColorHover, outline: 'none' }
				}}
				onClick={(evt) => {
					evt.preventDefault();
					action && action(sharingButtonElement);
				}}
				onKeyDown={({ code }) => {
					/**
					 * Both 'Enter' (link) and 'Space' (button) should trigger the action when using an anchor tag with button role
					 * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/button_role#accessibility_concerns
					 */
					if (code === 'Enter' || code === 'Space') {
						action && action();
					}
				}}
			>
				<Icon
					width="1.5rem"
					height="1.5rem"
					fontSize={'1rem'}
					color={themeContext.sharingMenu.menu.button.iconColor}
					code={Icons[icon]}
					marginRight="0.38rem"
				/>
				<Box
					className="label"
					color={themeContext.sharingMenu.menu.button.textColor}
					textTransform="uppercase"
					fontSize="0.875rem"
					fontStyle="normal"
					fontWeight="700"
					lineHeight="0.875rem"
					letterSpacing="0.08313rem"
				>
					{label}
				</Box>
			</Box>
		);
	};

	return (
		<Box as="li" padding="0.5rem 0">
			{type === 'facebook' &&
				sharingButton({
					label: 'Facebook',
					title: 'Diese Seite auf Facebook teilen',
					icon: 'facebook',
					actionUrl: actionUrlFacebook(sharingUrl, sharingTitle),
					action: (sharingButtonElement) => {
						openShareWindow(actionUrlFacebook(sharingUrl, sharingTitle));
						customWindow.app.dispatchEvent(customWindow.Application.EVENT_TRACKING, {
							name: 'social',
							type: 'social_fv',
							url: sharingButtonElement?.getAttribute('href'),
							platform: 'Facebook'
						});
					}
				})}

			{type === 'twitter' &&
				sharingButton({
					label: 'X',
					title: 'Diese Seite auf X teilen',
					icon: 'twitter',
					actionUrl: actionUrlTwitter(sharingUrl, sharingTitle),
					action: (sharingButtonElement) => {
						openShareWindow(actionUrlTwitter(sharingUrl, sharingTitle));
						customWindow.app.dispatchEvent(customWindow.Application.EVENT_TRACKING, {
							name: 'social',
							type: 'social_tw',
							url: sharingButtonElement?.getAttribute('href'),
							platform: 'Twitter'
						});
					}
				})}

			{type === 'whatsapp' &&
				sharingButton({
					label: 'WhatsApp',
					title: 'Diese Seite per WhatsApp teilen',
					icon: 'whatsapp',
					actionUrl: actionUrlWhatsApp(sharingUrl, sharingTitle),
					action: (sharingButtonElement) => {
						openShareWindow(actionUrlWhatsApp(sharingUrl, sharingTitle));
						customWindow.app.dispatchEvent(customWindow.Application.EVENT_TRACKING, {
							name: 'social',
							type: 'social_wa',
							url: sharingButtonElement?.getAttribute('href'),
							platform: 'WhatsApp'
						});
					}
				})}

			{type === 'mail' &&
				sharingButton({
					label: 'E-Mail',
					title: 'Diese Seite per E-Mail teilen',
					icon: 'mail',
					actionUrl: actionUrlMail(sharingUrl, sharingTitle, sharingDescription),
					action: (sharingButtonElement) => {
						document.location.href = actionUrlMail(sharingUrl, sharingTitle, sharingDescription);
						customWindow.app.dispatchEvent(customWindow.Application.EVENT_TRACKING, {
							name: 'social',
							type: 'social_mail',
							url: sharingButtonElement?.getAttribute('href'),
							platform: 'Email'
						});
					}
				})}

			{type === 'print' &&
				sharingButton({
					label: 'Drucken',
					title: 'Diese Seite drucken',
					icon: 'print',
					action: (sharingButtonElement) => {
						customWindow.print();
						customWindow.app.dispatchEvent(customWindow.Application.EVENT_TRACKING, {
							name: 'social',
							type: 'social_print',
							item: sharingButtonElement,
							url: 'print',
							platform: 'Print'
						});
					}
				})}
		</Box>
	);
};

export default SharingOption;
